'use client';

import { useField } from 'formik';
import { useTranslations } from 'next-intl';

import { FormikCheckbox as Checkbox } from '@commons/components/Form/CheckBox';
import { asAbsoluteURL } from '@commons/helpers/url';
import { API_WEBSITE } from '@commons/utils/constants';

import Link from '../../Link';
import Text from '../../Text';
import { type CheckboxProps } from '../CheckBox';

/**
 * ConsentLabel component.
 *
 * This component renders a consent label with a link to the privacy notice.
 */

export const ConsentLabel = () => {
  const t = useTranslations('forms.fields.consent');

  const privacyNoticeURL = asAbsoluteURL(t(`privacyNotice.alias.${API_WEBSITE}`));
  const label = t.rich('label', {
    privacyNotice: (chunks) => <Link href={privacyNoticeURL}>{chunks}</Link>,
  });

  return (
    <Text as="span" level="body-sm" marginBottom="none">
      {label}
    </Text>
  );
};

/**
 * FormikConsentCheckbox component.
 *
 * This component renders a Consent checkbox within a Formik form, which contains a consent label.
 * The checkbox uses Formik's field state and error handling.
 */

export const FormikConsentCheckbox = ({ name, error, ...props }: CheckboxProps) => {
  const [field, meta, helpers] = useField(name);

  const { setValue } = helpers;

  const isError = error || (meta.touched && meta.error !== undefined);

  return (
    <Checkbox onCheckedChange={setValue} error={isError} {...field} {...props}>
      <ConsentLabel />
    </Checkbox>
  );
};
